import { useMutation } from '@vue/apollo-composable';
import gql from '@/api/objects/item.graphql';
import gqlUser from '@/api/users/item.graphql';
import useCommonModal from '@/compositions/useCommonModal';
import useHistory from './useDownloadHistory';
import useMedia from '@/compositions/useMedia';
import axios from 'axios';
import hideIfEmpty from '../../utils/hideIfEmpty';

const useObjectActions = (props, context, entity) => {
  const { mutate: muteObject } = useMutation(gql.muteObject);
  const { mutate: updateObject } = useMutation(gql.update);
  const { mutate: deleteObject } = useMutation(gql.remove);
  const { mutate: unlinkObject } = useMutation(gql.disconnectObjectsToObject);
  const { mutate: unlinkObjToUser } = useMutation(gqlUser.deleteUsersToObject);

  const modalComposition = useCommonModal();
  const historyComposition = useHistory();
  const { downloadMediaAsFile, uploadMedia } = useMedia();

  const isEnabled = targetObject => {
    return targetObject.enabled;
  };

  const isMuted = targetObject => {
    return targetObject.muted;
  };

  const isSourceCode = targetObject => {
    return targetObject.mediaType === 'text/x-python-script';
  };

  const isSchedule = targetObject => {
    return targetObject?.schema?.mTags?.includes('schedule');
  };

  const isHardLinked = (actionItem, targetObject) => {
    if (actionItem.showWhenHardLinked && targetObject.hardLinked) {
      return true;
    }

    if (!targetObject.hardLinked) return true;
  };

  const isShowUnlink = targetObject => {
    if (targetObject.connectionObjToUserId) {
      return {
        id: 'remove',
        text: 'Unlink',
        icon: 'mdi-reply',
        handleAction: () =>
          handleUnlinkObjToUser(targetObject.connectionObjToUserId),
        _enabled: true
      };
    }

    if (targetObject.connectionId) {
      return {
        id: 'remove',
        text: 'Unlink',
        icon: 'mdi-reply',
        handleAction: () => handleUnlink(targetObject.connectionId),
        _enabled: true
      };
    }
    return null;
  };

  const handleEditMassiveProperties = (properties, object) => {
    modalComposition.modal.show({
      component: () => import('@/components/app-entity/modals/AppDialog.vue'),
      bind: {
        nameModal: 'ChangeProperties'
      },
      slots: {
        default: {
          component: () =>
            import('@/components/app-entity/modals/EditProperties'),
          bind: {
            properties,
            objectId: object.id,
            object
          },
          on: {
            close() {
              modalComposition.modal.hide('ChangeProperties');
            }
          }
        }
      }
    });
  };

  const generateEditGroup = object => {
    let unicGroupName = [];
    let insertedItem = [];

    if (object.objectProperties) {
      const groupNames = object.objectProperties.map(item => item.groupName);
      unicGroupName = new Set(groupNames);
    }

    unicGroupName.forEach(item => {
      const properties = object.objectProperties
        .filter(itemProperty => item === itemProperty.groupName)
        .map(itemMap => ({
          ...itemMap,
          title: humanReadableFlag(itemMap.property, object)
        }));

      insertedItem.push({
        id: 'massive-editgroup',
        text: `Edit ${item}`,
        showWhenHardLinked: false,
        name: item,
        icon: 'mdi-publish',
        handleAction: () => handleEditMassiveProperties(properties, object),
        _enabled: !object.connectionObjToUserId
      });
    });
    return insertedItem;
  };

  const processPipeline = (targetObject, arrayActions) => {
    return arrayActions
      .filter(item => item)
      .filter(item => isHardLinked(item, targetObject));
  };

  const humanReadableFlag = (nonHuman, object) => {
    let schema = object?.schema;
    if (!schema) {
      return nonHuman;
    } else {
      const associateProperty = schema.schemaProperties.find(
        item => item.property === nonHuman
      );
      if (associateProperty && associateProperty.description) {
        return associateProperty.description;
      }
      return nonHuman;
    }
  };

  const handleMuteObject = targetObject => {
    muteObject({
      input: {
        objectsId: [targetObject.id],
        muted: !isMuted(targetObject)
      }
    }).then(() => {
      if (targetObject.connectionId) {
        entity.restart();
      }
    });
  };

  const handleDisableObject = targetObject => {
    updateObject({
      input: {
        id: targetObject.id,
        patch: {
          enabled: !isEnabled(targetObject)
        }
      }
    }).then(() => {
      if (targetObject.connectionId) {
        entity.restart();
      }
    });
  };

  const handleUnlink = connectionId => {
    unlinkObject({
      input: {
        id: connectionId
      }
    });
  };

  const handleUnlinkObjToUser = connectionId => {
    unlinkObjToUser({
      input: {
        id: connectionId
      }
    });
  };

  const handleDeleteObject = targetObject => {
    modalComposition.modal.show({
      component: () => import('@/components/app-entity/modals/AppDialog.vue'),
      bind: {
        nameModal: 'DeleteObject'
      },
      slots: {
        default: {
          component: () =>
            import('@/components/app-entity/modals/DefaultConfirmation'),
          bind: {
            textDelete: 'Delete this object completely?'
          },
          on: {
            delete() {
              deleteObject({
                input: {
                  id: targetObject.id
                }
              }).then(() => {
                modalComposition.modal.hide('DeleteObject');

                if (!targetObject.connectionId) {
                  context.root.$router.replace({
                    name: context.root.$route.meta.rootRouteName,
                    query: context.root.$route.query
                  });
                }
              });
            },
            close() {
              modalComposition.modal.hide('DeleteObject');
            }
          }
        }
      }
    });
  };

  const handleEdit = targetObject => {
    let component = null;

    switch (targetObject.schemaType) {
      case 'media':
        component = import('@/components/app-entity/modals/CreateMedia');
        break;
      default:
        component = import('@/components/app-entity/modals/CreateObject');
    }

    if (targetObject?.schema?.mTags?.includes('schedule')) {
      component = import('@/components/app-entity/modals/CreateSchedule.vue');
    }

    modalComposition.modal.show({
      component: () => import('@/components/app-entity/modals/AppDialog.vue'),
      bind: {
        nameModal: 'EditObject'
      },
      slots: {
        default: {
          component: () => component,
          bind: {
            entity: targetObject,
            mediaType: targetObject.mediaType
          },
          on: {
            close() {
              modalComposition.modal.hide('EditObject');
            },
            async save(e) {
              const processImages = () => {
                if (e.toggleLoading) e.toggleLoading(true);
                if (e.formData) {
                  return axios.request({
                    method: 'POST',
                    url: `${window.__pixelConfig.APP_MEDIA_SERVER}/upload/${
                      e.id
                    }/${localStorage.getItem('authToken')}`,
                    headers: {
                      'Content-Type': 'multipart/form-data'
                    },
                    data: e.formData
                  });
                } else {
                  return Promise.resolve(true);
                }
              };

              processImages()
                .then(() => {
                  return updateObject({
                    input: {
                      id: e.id,
                      patch: {
                        enabled: e.enabled,
                        name: e.name,
                        description: e.description
                      }
                    }
                  });
                })
                .then(() => {
                  if (e.schemaType === 'media') {
                    context.root.$router.push({
                      name: 'mediasTableComponent',
                      query: {
                        t: Date.now()
                      }
                    });
                  }
                  modalComposition.modal.hide('EditObject');
                })
                .finally(() => {
                  if (e.toggleLoading) e.toggleLoading(false);
                });

              if (targetObject.connectionId) {
                entity.restart();
              }
            }
          }
        }
      }
    });
  };

  const handleEditSchedule = targetObject => {
    modalComposition.modal.show({
      component: () => import('@/components/app-entity/modals/AppDialog.vue'),
      bind: {
        nameModal: 'EditObject'
      },
      slots: {
        default: {
          component: () =>
            import('@/components/app-entity/modals/CreateSchedule.vue'),
          bind: {
            entity: targetObject
          },
          on: {
            close() {
              modalComposition.modal.hide('EditObject');
            }
          }
        }
      }
    });
  };

  const handleChangeSchema = targetObject => {
    modalComposition.modal.show({
      component: () => import('@/components/app-entity/modals/AppDialog.vue'),
      bind: {
        nameModal: 'ChangeScheme'
      },
      slots: {
        default: {
          component: () => import('@/components/app-entity/modals/EditScheme'),
          bind: {
            schemaId: targetObject.schemaId,
            objectId: targetObject.id,
            type: targetObject.schemaType.toUpperCase()
          },
          on: {
            close() {
              modalComposition.modal.hide('ChangeScheme');
            }
          }
        }
      }
    });
  };

  const handleEditGroupAccess = targetObject => {
    modalComposition.modal.show({
      component: () => import('@/components/app-entity/modals/AppDialog.vue'),
      bind: {
        nameModal: 'EditRightsGroup'
      },
      slots: {
        default: {
          component: () =>
            import('@/components/app-entity/modals/EditRightsGroup'),
          bind: {
            entity: targetObject
          },
          on: {
            close() {
              modalComposition.modal.hide('EditRightsGroup');
            },
            save() {
              modalComposition.modal.hide('EditRightsGroup');
            }
          }
        }
      }
    });
  };

  const handleEditSource = targetObject => {
    downloadMediaAsFile(targetObject.id).then(data => {
      modalComposition.modal.show({
        component: () => import('@/components/app-entity/modals/AppDialog.vue'),
        bind: {
          contentClass: 'fullscreen',
          nameModal: 'EditSource'
        },
        slots: {
          default: {
            component: () =>
              import('@/components/app-entity/modals/FullscreenCode'),
            bind: {
              code: data.data
            },
            on: {
              save(code) {
                const blob = new Blob([code], { type: targetObject.mediaType });

                uploadMedia(
                  blob,
                  null,
                  targetObject.id,
                  targetObject.mediaName
                ).then(() => {
                  modalComposition.modal.hide('EditSource');
                });
              },
              close() {
                modalComposition.modal.hide('EditSource');
              }
            }
          }
        }
      });
    });
  };

  const getActions = targetEntity => {
    let actions = [
      {
        id: 'EditObject',
        text: 'Edit',
        icon: 'mdi-pencil',
        _enabled: !targetEntity.connectionObjToUserId,
        showWhenHardLinked: false,
        handleAction: () => {
          if (targetEntity?.schema?.mTags?.includes('schedule')) {
            handleEditSchedule(targetEntity);
          } else {
            handleEdit(targetEntity);
          }
        }
      },
      {
        id: 'change-group-access',
        text: 'Change access',
        icon: 'mdi-security',
        _enabled: !targetEntity.connectionObjToUserId,
        showWhenHardLinked: false
        // handleAction: () => handleEditGroupAccess(targetEntity)
      },
      ...hideIfEmpty(!isSchedule(targetEntity), [
        {
          id: 'change-scheme',
          text: 'Change schema',
          icon: 'mdi-card-bulleted',
          handleAction: () => handleChangeSchema(targetEntity),
          _enabled: true,
          showWhenHardLinked: false
        }
      ]),

      {
        id: 'enable-disable',
        text: isEnabled(targetEntity) ? 'Disable' : 'Enable',
        handleAction: () => handleDisableObject(targetEntity),
        icon: 'mdi-cancel',
        _enabled: true,
        showWhenHardLinked: false
      },
      {
        id: 'send-noty',
        text: 'Create notification',
        icon: 'mdi-send',
        _enabled: true,
        showWhenHardLinked: false
      },
      {
        id: 'mute-unmute',
        text: isMuted(targetEntity) ? 'Unmute' : 'Mute',
        icon: isMuted(targetEntity) ? 'mdi-volume-high' : 'mdi-volume-off',
        handleAction: () => handleMuteObject(targetEntity),
        _enabled: true,
        showWhenHardLinked: false
      },
      ...hideIfEmpty(isSourceCode(targetEntity), [
        {
          id: 'edit-source-code',
          text: 'Edit code',
          icon: 'mdi-code-tags',
          handleAction: () => handleEditSource(targetEntity),
          _enabled: true
        }
      ]),
      {
        text: 'Show history',
        id: 'show-property-history',
        icon: 'mdi-history',
        _enabled: !targetEntity.connectionObjToUserId,
        showWhenHardLinked: true
      },
      {
        id: 'show-history',
        text: 'Show controls history',
        icon: 'mdi-history',
        _enabled: !targetEntity.connectionObjToUserId,
        showWhenHardLinked: true
      },
      {
        id: 'download-notifications',
        text: 'Download notifications',
        icon: 'mdi-download',

        handleAction: () =>
          historyComposition.downloadNotificationsHistoryOfObjects({
            value: [
              {
                id: targetEntity.id
              }
            ]
          }),
        _enabled: true
      },
      {
        divider: true,
        showWhenHardLinked: false
      },
      ...generateEditGroup(targetEntity),
      {
        divider: true,
        showWhenHardLinked: false
      },
      {
        id: 'copy-uuid',
        text: 'Copy UUID',
        icon: 'mdi-image-filter-none',
        _enabled: true,
        showWhenHardLinked: true
      },
      isShowUnlink(targetEntity),
      {
        id: 'delete',
        text: 'Delete',
        icon: 'mdi-delete',
        handleAction: () => handleDeleteObject(targetEntity),
        _enabled: true,
        showWhenHardLinked: false
      },
      ...hideIfEmpty(targetEntity.schemaType === 'media', [
        {
          id: 'download-file',
          text: 'Download file',
          icon: 'mdi-download',
          _enabled: true
        }
      ])
    ];

    return processPipeline(targetEntity, actions);
  };
  return {
    getActions
  };
};

export default useObjectActions;
